'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AlertTriangle, ArrowRight, Info, Medal, PackageX, ShoppingBag } from 'lucide-react'
import Input from '@/components/elements/Input'
import { Button } from '@/components/elements/Button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCart, useLoginForm } from '@/lib/commerce/hooks'
import { formatPrice } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import CheckoutCard from './CheckoutCard'

export default function CheckoutLogin({ vendor }: { vendor: any }) {
  const pathname = usePathname() || ''
  const { cart, initialLoading, isValidating, validateCart, canCheckout, orderDiscountAmount, cartTotal, amount, sale, loyaltyDiscountAmount } = useCart()
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - orderDiscountAmount)) : 0
  const f = useLoginForm({ defaultRedirect: '/checkout' })
  const [mounted, setMounted] = useState(false)

  const products = cart?.products || []

  useEffect(() => { setMounted(true) }, [])
  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!mounted) return null

  const total = Math.max(0, cartTotal - (orderDiscountAmount || 0) - loyaltyDiscount)
  const hasDigitalOnly = products.length > 0 && products.every((p: any) => p.isDigital === true)
  const hasOutOfStock = products.some((p: any) => p.quantity === 0 || p.isOutOfStock)

  return (
    <section className="bg-cream pt-6 pb-16 min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <a href={navigateUrl('/cart', pathname)} className="hover:text-foreground">Сагс</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Нэвтрэх</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-12">
          <div>
            <h1 className="font-bold font-gilroy tracking-[-0.025em] leading-[1.05] text-foreground" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
              Нэвтрэх
            </h1>
            <p className="mt-3 text-[14px] text-foreground/60">
              Худалдан авалтаа үргэлжлүүлэхийн тулд нэвтэрнэ үү.
            </p>

            <form onSubmit={f.handleLogin} className="mt-7 flex flex-col gap-4 max-w-md">
              <Input label="Имэйл эсвэл утасны дугаар" type="text" placeholder="example@shop.mn" value={f.emailOrPhone} onChange={(e: any) => f.setEmailOrPhone(e.target.value)} onKeyDown={f.handleKeyDown} autoFocus />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium leading-none">Нууц үг</span>
                  <Link href={navigateUrl('/forgot', pathname)} className="text-[13px] font-medium text-foreground underline underline-offset-[3px] hover:text-foreground/70 transition">
                    Мартсан уу?
                  </Link>
                </div>
                <Input type="password" placeholder="••••••••" value={f.password} onChange={(e: any) => f.setPassword(e.target.value)} onKeyDown={f.handleKeyDown} />
              </div>
              <Button type="submit" isLoading={f.loading} icon={ArrowRight} iconPosition="right" variant="outline" className="mt-2 w-full h-14 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all" loadingMessage="Нэвтэрч байна...">
                Нэвтрэх
              </Button>

              {vendor?.canGuestOrder && (
                <>
                  <div className="flex items-center justify-center gap-3 my-1">
                    <div className="flex-1 h-px bg-foreground/10" />
                    <span className="text-[12px] text-foreground/50">эсвэл</span>
                    <div className="flex-1 h-px bg-foreground/10" />
                  </div>
                  <button
                    type="button"
                    onClick={() => { window.location.href = navigateUrl('/checkout', pathname) }}
                    disabled={isValidating || !canCheckout()}
                    className="w-full h-12 rounded-full bg-white border border-foreground/15 hover:border-foreground/40 transition-colors text-[14px] font-semibold text-foreground inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Зочноор үргэлжлүүлэх
                  </button>
                </>
              )}

              <p className="mt-2 text-[14px] text-center text-foreground/65">
                Шинэ хэрэглэгч үү?{' '}
                <span onClick={f.routeToRegister} className="cursor-pointer font-semibold text-foreground underline underline-offset-[3px] hover:text-foreground/80 transition-colors">
                  Бүртгүүлэх
                </span>
              </p>
            </form>

            {vendor?.canGuestOrder && (
              <div className="mt-6 max-w-md flex gap-2.5 p-3.5 rounded-2xl bg-amber-50 border border-amber-100 text-amber-900">
                <Info className="size-4 shrink-0 mt-0.5" />
                <div className="text-[12.5px] leading-5">
                  Зочноор үргэлжлүүлсэн тохиолдолд таны захиалгын түүх хадгалагдахгүйг анхаарна уу.
                </div>
              </div>
            )}
          </div>

          <aside>
            <div className="bg-white rounded-3xl p-6 lg:p-7">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/55 mb-1.5">Захиалга</div>
              <div className="text-[22px] font-bold font-gilroy tracking-[-0.015em] text-foreground mb-5">Хураангуй</div>

              {initialLoading ? (
                <div className="space-y-5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="grid grid-cols-[72px_1fr_auto] gap-4 items-start">
                      <Skeleton className="w-18 h-18 rounded-2xl" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-7 w-28 rounded-full mt-1.5" />
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  ))}
                  <div className="pt-4 border-t border-foreground/10 space-y-2">
                    <div className="flex justify-between"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-20" /></div>
                    <div className="flex justify-between"><Skeleton className="h-4 w-16" /><Skeleton className="h-4 w-24" /></div>
                    <div className="flex justify-between"><Skeleton className="h-5 w-20" /><Skeleton className="h-7 w-28" /></div>
                  </div>
                </div>
              ) : products.length === 0 ? (
                <div className="py-10 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-3xl bg-[#F1EEE7] inline-flex items-center justify-center mb-4">
                    <ShoppingBag className="size-7 text-foreground/40" />
                  </div>
                  <div className="text-[15px] font-semibold text-foreground">Таны сагс хоосон байна</div>
                  <div className="mt-1 text-[13px] text-foreground/55">Та яг одоо сагсандаа бараа нэмээрэй!</div>
                  <a href={navigateUrl('/product', pathname)} className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[13px] font-semibold hover:bg-foreground/90 transition-colors">
                    <ShoppingBag className="size-3.5" /> Дэлгүүр хэсэх
                  </a>
                </div>
              ) : (
                <>
                  <div>
                    {products.map((p: any) => (
                      <CheckoutCard key={`${p.isRewardItem ? 'reward-' : ''}${p.id}-${p.variant?.id}`} product={p} />
                    ))}
                  </div>

                  {(() => {
                    const subPrice = (amount || 0) - (sale || 0)
                    const belowMin = vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount
                    if (!belowMin) return null
                    return (
                      <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
                        <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-[12.5px] leading-snug">
                          <div className="font-semibold text-amber-800">Захиалгын доод дүнд хүрээгүй байна</div>
                          <div className="text-amber-700 mt-0.5">Хамгийн багадаа <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount)}</span>-ийн захиалга хийх ёстой. <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subPrice)}</span> дутуу байна.</div>
                          <div className="mt-2 h-1.5 rounded-full bg-amber-200 overflow-hidden">
                            <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, (subPrice / vendor.orderMinAmount) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })()}

                  <div className="mt-5 pt-5 border-t border-foreground/10 flex flex-col gap-2 text-[14px]">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground/60">Үнийн дүн</span>
                      <span className="text-foreground font-medium">{formatPrice(cartTotal)}</span>
                    </div>
                    {!hasDigitalOnly && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Хүргэлт</span>
                        <span className="text-foreground/60">Хүргэлтийн хаяг оруулах</span>
                      </div>
                    )}
                    {orderDiscountAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Захиалгын хөнгөлөлт</span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(orderDiscountAmount)}</span>
                      </div>
                    )}
                    {loyaltyDiscount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 text-violet-600 font-medium"><Medal className="size-4" /> Лояалти хөнгөлөлт</span>
                        <span className="text-violet-600 font-medium tabular-nums">−{formatPrice(loyaltyDiscount)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-foreground/10 flex items-baseline justify-between">
                    <span className="text-[15px] font-semibold text-foreground">Нийт дүн</span>
                    <span className="text-[28px] font-bold font-gilroy tracking-[-0.02em] text-foreground">{formatPrice(total)}</span>
                  </div>

                  {hasOutOfStock && (
                    <div className="mt-4 p-3.5 rounded-2xl bg-red-50 border border-red-100 flex items-start gap-3">
                      <PackageX className="size-4 text-red-500 shrink-0 mt-0.5" />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] font-semibold text-red-600 leading-5">Үлдэгдэл дууссан бараа</span>
                        <span className="text-[12.5px] text-red-500 leading-5">Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна. Шалгаад үргэлжлүүлнэ үү.</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  )
}
